import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import { StyledUserDataDeletion } from './styles';
import { TextLinkColor } from '../../../components/atoms/text-link-color';
import { bemClassName } from '../../../utils/bem';
import { SUPPORT_EMAIL } from '../../../utils/constants';
import { canonical, ogImage, ogUrl } from '../../../utils/seo';

const b = bemClassName('user-data-deletion-page');

const UserDataDeletion = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <StyledUserDataDeletion>
      <Helmet>
        <title>Data Deletion Request — Manage Your Data | Active Shape</title>
        <meta
          name="description"
          content="Learn how to request deletion of your Active Shape account and personal data, and understand our data retention policies."
        />
        {canonical('/user-data-deletion') && <link rel="canonical" href={canonical('/user-data-deletion')} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Data Deletion Request — Manage Your Data | Active Shape" />
        <meta
          property="og:description"
          content="Learn how to request deletion of your Active Shape account and personal data, and understand our data retention policies."
        />
        {ogImage() && <meta property="og:image" content={ogImage()} />}
        {ogUrl('/user-data-deletion') && <meta property="og:url" content={ogUrl('/user-data-deletion')} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Data Deletion Request — Manage Your Data | Active Shape" />
        <meta
          name="twitter:description"
          content="Learn how to request deletion of your Active Shape account and personal data, and understand our data retention policies."
        />
        {ogImage() && <meta name="twitter:image" content={ogImage()} />}
      </Helmet>
      <div className={b()}>
        <div className={b('card')}>
          <h1 className={b('title')}>User Data Deletion Guidelines and Policies</h1>
          <p className={b('update')}>Last updated: March 12, 2026</p>

          <div className={b('nav-links')}>
            <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
            <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
          </div>

          <p>
            At <strong>Active Shape</strong>, we respect your right to control your personal information. This policy
            explains how you can delete your account and data, what gets deleted, and what your rights are.
          </p>

          <h2 className={b('section-title')}>1. What Data Is Deleted</h2>
          <p>When you delete your account, the following data is permanently removed:</p>
          <ul>
            <li>Profile data (name, email, photo, health information)</li>
            <li>Daily tracking history (weight, nutrition, water, steps)</li>
            <li>Meal logs and AI food analysis records</li>
            <li>Workout history and favorites</li>
            <li>Fasting sessions and supplement logs</li>
            <li>AI-generated insights and personalized plans</li>
            <li>Notifications and push tokens</li>
            <li>Payment methods and subscription records</li>
            <li>Referral codes, referral records, and pending payouts</li>
            <li>Bug reports (including uploaded screenshots)</li>
            <li>Authentication tokens and session data</li>
          </ul>
          <p>
            API usage logs are anonymized (your user ID is removed) but aggregate records are retained for analytics.
          </p>

          <h2 className={b('section-title')}>2. How to Delete Your Account</h2>

          <h3>2.1 From the app or website (recommended)</h3>
          <p>
            <strong>Web:</strong> Go to <strong>Settings</strong> → <strong>Account</strong> tab →{' '}
            <strong>Delete My Account</strong>.
          </p>
          <p>
            <strong>Mobile:</strong> Go to <strong>Settings</strong> → scroll to the bottom → tap{' '}
            <strong>Delete Account</strong>.
          </p>
          <p>
            You will be asked to confirm. Once confirmed, your account and all associated data will be deleted
            immediately.
          </p>

          <h3>2.2 By email</h3>
          <p>
            You can also send an email with the subject <strong>Data Deletion Request</strong> to:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <p>You must include:</p>
          <ul>
            <li>Your full name</li>
            <li>Email address associated with the account</li>
            <li>Explicit confirmation that you want to delete your data</li>
          </ul>

          <h2 className={b('section-title')}>3. What Happens During Deletion</h2>
          <ol>
            <li>
              <strong>Active subscriptions are cancelled immediately</strong> via Stripe. No further charges will be
              made.
            </li>
            <li>
              <strong>Stripe customer record is deleted</strong>, including saved payment methods.
            </li>
            <li>
              <strong>Stripe Connect account is deleted</strong> (if you participated in the referral program). Any
              pending payouts are forfeited.
            </li>
            <li>
              <strong>All personal data is deleted</strong> from our databases.
            </li>
            <li>
              <strong>Uploaded files are deleted</strong> from cloud storage (e.g., bug report screenshots).
            </li>
            <li>
              <strong>Usage logs are anonymized</strong> — your user ID is removed but aggregate data is kept for
              analytics.
            </li>
          </ol>

          <h2 className={b('section-title')}>4. Deletion Timeframes</h2>
          <ul>
            <li>
              <strong>In-app deletion:</strong> Immediate — your data is deleted as soon as you confirm.
            </li>
            <li>
              <strong>Email requests:</strong> Processed within 30 business days.
            </li>
            <li>
              <strong>Backup deletion:</strong> Backups are purged on a rolling 90-day cycle.
            </li>
          </ul>

          <h2 className={b('section-title')}>5. Data That May Be Temporarily Retained</h2>
          <p>
            Some data may be temporarily retained due to legal obligations, audits, fraud prevention, or security
            reasons. This data is blocked and not used for any commercial activity.
          </p>

          <h2 className={b('section-title')}>6. Automatic Deletion</h2>
          <p>
            Some data is automatically deleted after periods of inactivity, such as expired session tokens, old
            sessions, or logs older than 90 days.
          </p>

          <h2 className={b('section-title')}>7. Consequences of Deletion</h2>
          <p>Once deletion is complete:</p>
          <ul>
            <li>You will not be able to recover your account or data.</li>
            <li>Any active subscription will be cancelled immediately with no refund for the remaining period.</li>
            <li>Any pending referral payouts will be forfeited.</li>
            <li>All your history, preferences, and settings will be permanently lost.</li>
            <li>You will need to create a new account if you want to use Active Shape again.</li>
          </ul>

          <h2 className={b('section-title')}>8. Contact</h2>
          <p>
            If you have questions about data deletion, you can write to us at:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>

          <div className={b('footer')}>
            <p>Related documents:</p>
            <div className={b('footer', 'links')}>
              <TextLinkColor to="/terms-of-service">Terms of Service</TextLinkColor>
              <TextLinkColor to="/privacy-policy">Privacy Policy</TextLinkColor>
            </div>
          </div>
        </div>
      </div>
    </StyledUserDataDeletion>
  );
};

export default UserDataDeletion;
